import React from 'react';
import { OnChangeCallback } from '../../../../../../util/UtilityTypes'
import BoolInput from '../../../../../misc/BoolInput';
import IntInput from '../../../../../misc/IntInput';
import EnumInput from '../../../../../misc/EnumInput';
import TextInput from '../../../../../misc/TextInput';
import * as GoogleUser from '../../../../../../util/GoogleUserExportTypes';
import { update } from '../../../../../../util/Helper';
import { useTranslation } from 'react-i18next';

interface GoogleUserPasswordPolicyProps {
    passwordPolicy: GoogleUser.PasswordPolicy
    configurationType: string
    disabled?: boolean
    validation: string[]
    onChange: OnChangeCallback<GoogleUser.PasswordPolicy>
}

const GoogleUserPasswordPolicy: React.FC<GoogleUserPasswordPolicyProps> = (props) => {
    const { t } = useTranslation(['configuration']);

    const renderTypeContent = () => {
        switch (props.passwordPolicy.type) {
            case GoogleUser.PasswordPolicyType.STATIC: 
                return (<>
                    <hr />
                    <h5>{t('googleUserExport.passwordPolicy.static.title')}</h5>
                    <TextInput
                        title={t('googleUserExport.passwordPolicy.static.staticValue.title')}
                        name={`${props.configurationType}-passwordPolicy.staticValue`}
                        value={props.passwordPolicy.staticValue}
                        updateValue={(n, v) => update(n, props.passwordPolicy, { staticValue: v }, props.onChange)}
                        disabled={props.disabled}
                        invalidFeedback={t('googleUserExport.passwordPolicy.static.staticValue.invalid')}
                        invalid={props.validation.indexOf(`${props.configurationType}.passwordPolicy.staticValue`) !== -1}
                    />
                    <BoolInput
                        title={t('googleUserExport.passwordPolicy.static.useMonthAndDay')}
                        name={`${props.configurationType}-passwordPolicy.useMonthAndDayFromSocialnumber`}
                        value={props.passwordPolicy.useMonthAndDayFromSocialnumber}
                        updateValue={(n, v) => update(n, props.passwordPolicy, { useMonthAndDayFromSocialnumber: v }, props.onChange)}
                        disabled={props.disabled}
                        titleSmall={true}
                    />
                </>);
            case GoogleUser.PasswordPolicyType.RANDOM: 
                return (<>
                    <hr />
                    <h5>{t('googleUserExport.passwordPolicy.random.title')}</h5>
                    <IntInput
                        title={t('googleUserExport.passwordPolicy.random.minLength')}
                        name={`${props.configurationType}-passwordPolicy.minLength`}
                        value={props.passwordPolicy.minLength}
                        disabled={props.disabled}
                        updateValue={(n, v) => update(n, props.passwordPolicy, { minLength: v }, props.onChange)}
                        invalidFeedback={t('googleUserExport.passwordPolicy.random.invalidMinLength')}
                        invalid={props.validation.indexOf(`${props.configurationType}.passwordPolicy.minLength`) !== -1}
                    />
                    <IntInput
                        title={t('googleUserExport.passwordPolicy.random.maxLength')}
                        name={`${props.configurationType}-passwordPolicy.maxLength`}
                        value={props.passwordPolicy.maxLength}
                        disabled={props.disabled}
                        updateValue={(n, v) => update(n, props.passwordPolicy, { maxLength: v }, props.onChange)}
                        invalidFeedback={t('googleUserExport.passwordPolicy.random.invalidMaxLength')}
                        invalid={props.validation.indexOf(`${props.configurationType}.passwordPolicy.maxLength`) !== -1}
                    />
                    <EnumInput
                        title={t('googleUserExport.passwordPolicy.random.characterSet')}
                        name={`${props.configurationType}-passwordPolicy.passwordCharacterSets`}
                        value={props.passwordPolicy.passwordCharacterSets}
                        options={GoogleUser.passwordCharacterSets.map(x => ({value: x, text: t(`googleUserExport.passwordPolicy.passwordCharacterSetOptions.${x}`)}) )}
                        onChange={(n, v) => update(n, props.passwordPolicy, { passwordCharacterSets: v }, props.onChange)}
                        disabled={props.disabled}
                        multi={true}
                        invalidFeedback={t('googleUserExport.passwordPolicy.random.invalidCharacterSet')}
                        invalid={props.validation.indexOf(`${props.configurationType}.passwordPolicy.passwordCharacterSets`) !== -1}
                    />
                </>);
            default: return null;
        }
    }

    return (<>
        <BoolInput
            title={t('googleUserExport.passwordPolicy.forceChangeOnNextLogin')}
            name={`${props.configurationType}-passwordPolicy.forcePasswordChangeOnNextLogin`}
            value={props.passwordPolicy.forcePasswordChangeOnNextLogin}
            updateValue={(n, v) => update(n, props.passwordPolicy, { forcePasswordChangeOnNextLogin: v }, props.onChange)}
            disabled={props.disabled}
        />
        <EnumInput
            title={t('googleUserExport.passwordPolicy.type')}
            name={`${props.configurationType}-passwordPolicy.type`}
            value={props.passwordPolicy.type}
            options={GoogleUser.passwordPolicyTypeOptions.map(x => ({value: x, text: t(`googleUserExport.passwordPolicy.passwordTypeOptions.${x}`)}))}
            onChange={(n, v) => update(n, props.passwordPolicy, { type: v }, props.onChange)}
            disabled={props.disabled}
        />
        {renderTypeContent()}
    </>);
}

export default GoogleUserPasswordPolicy;
